/* eslint-disable react/jsx-indent */
/* eslint-disable indent */
import './style.scss';

import { useEffect, useMemo, useState } from 'react';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useMutation, useQuery } from 'react-query';
import { Link, useParams, useNavigate } from 'react-router-dom';
import ReactRouterPrompt from 'react-router-prompt';
import { yupResolver } from '@hookform/resolvers/yup';

import { Button } from 'primereact/button';
import { Dialog } from 'primereact/dialog';

import { Message } from 'primereact/message';
import { ConfirmDialog, confirmDialog } from 'primereact/confirmdialog';
import SendMailRemindAllMembersDialog from 'features/Council/components/SendRemindMembersDialog';
import { createOrUpdatePrequalificationCouncilSchema } from 'features/Council/validation';

import {
  changeRequestStatus,
  createOrUpdatePrequalificationCouncil,
  getPrequalificationCouncilOfProjectProposal,
  getAllCouncilTypes,
  downloadCouncilMemberExcelFile,
} from 'apis/councils.api';
import {
  downloadFile,
  getAllVersions,
  getReviseLogs,
  requestExplanation,
  updateExplanationRequest,
  generalInfoForCouncil,
} from 'apis/projectProposals.api';
import { getAll as getAllOrganizations } from 'apis/organizations.api';

import Members from 'features/Council/components/Detail/Members';
import { Can } from 'components/Can';
import ErrorDialog from 'components/ErrorDialog';
import Loading from 'components/Loading';

import { TIMEZONE_VN, PROJECT_ROLES, COUNCIL_STATUS, TOAST, COUNCIL_TYPE_CODE } from 'constant';
import {
  formatFileName,
  getFilterMembersAreAllowedReview,
  getNumberMembersPresentAndReviewed,
  getAcadamicLevelShortHand,
  getRoles,
  checkIsAllowedCRUDCouncil,
  formatDatetime,
  autoDownloadFile,
} from 'utils/func';
import OwnerExplanation from 'features/Council/components/OwnerExplanation';
// import RequestUpdateType from 'features/Council/components/RequestUpdateType';
import { toast } from 'layout';
import RichTextViewer from 'components/RichTextViewer';

const formOptions = { resolver: yupResolver(createOrUpdatePrequalificationCouncilSchema) };

export default function PrequalificationCouncil() {
  // #region Data
  const roles = getRoles();
  const { t } = useTranslation();
  const navigate = useNavigate();

  const [isVisibleErrorDialog, setIsVisibleDialogError] = useState(false);
  const [isVisibleConfirmSendRemind, setIsVisibleConfirmSendRemind] = useState(false);
  // const [isVisibleUpdate, setIsVisibleUpdate] = useState(false);
  // const [allowEditResult, setAllowEditResult] = useState(false);
  const { projectProposalId } = useParams();
  const Url = window.location.href;
  const isReadOnly = Url.includes('projects-submit');

  const {
    reset,
    watch,
    formState: { dirtyFields, isDirty },
  } = useForm({ ...formOptions, mode: 'onChange' });

  // const isOpenedInCouncilForm = watch('isOpened');

  const {
    handleSubmit: handleSubmitFeedback,
    control: feedbackControl,
    reset: resetFeedback,
    formState: {
      errors: feedbackErrors,
      dirtyFields: feedbackDirtyFields,
      isDirty: isFeedbackDirty,
      isValid: isFeedbackValid,
    },
  } = useForm({ mode: 'onChange' });

  // explanation request (projectProposalReviseLog): thông tin yêu cầu giải trình
  const {
    data: explanationRequestsData,
    isLoading: isExplanationRequestsLoading,
    isError: isExplanationRequestsError,
    error: explanationRequestsError,
    refetch: refetchExplanationRequests,
  } = useQuery(
    ['projectProposalReviseLogs', projectProposalId, 'ALL_REQUEST_EXPLANATION_PREQUALIFICATION'],
    () =>
      getReviseLogs({
        projectProposalId,
        types: ['REQUEST_EXPLANATION_PREQUALIFICATION'],
      }),
    { enabled: !!projectProposalId }
  );
  const explanationRequest = useMemo(() => {
    if (explanationRequestsData?.data?.length > 0) {
      return explanationRequestsData?.data?.[0];
    }
    return null;
  }, [explanationRequestsData]);

  // updateProjectTypeData (projectProposalReviseLogUpdateProjectType): thông tin cập nhật loại đề tài
  const {
    data: updateProjectTypeData,
    isLoading: isUpdateProjectTypeDataLoading,
    isError: isUpdateProjectTypeDataError,
    error: updateProjectTypeDataError,
    // refetch: refetchExplanationRequests,
  } = useQuery(
    ['projectProposalReviseLogUpdateProjectType', projectProposalId, 'REQUEST_UPDATE_PROJECT_TYPE'],
    () =>
      getReviseLogs({
        projectProposalId,
        types: ['REQUEST_UPDATE_PROJECT_TYPE'],
      }),
    { enabled: !!projectProposalId }
  );
  const updateProjectType = useMemo(
    () => updateProjectTypeData?.data[0] ?? null,
    [updateProjectTypeData]
  );

  // prequalification council (thông tin hội đồng sơ tuyển)
  const {
    data: councilData,
    isLoading: isPrequalificationCouncilLoading,
    isError: isPrequalificationCouncilError,
    error: prequalificationCouncilError,
    refetch: refetchCouncil,
  } = useQuery(['council', projectProposalId], () =>
    getPrequalificationCouncilOfProjectProposal({ projectProposalId })
  );

  const council = useMemo(() => councilData?.data || null, [councilData]);
  const councilId = council?.id;

  // project proposal version 2
  const { data: allProjectProposalVersionsData } = useQuery(
    ['allProjectProposalVersions', projectProposalId],
    () => getAllVersions(projectProposalId)
  );

  const [prequalificationVersion] = useMemo(() => {
    if (allProjectProposalVersionsData?.data?.length > 0) {
      return [allProjectProposalVersionsData?.data?.find((item) => item?.version === 2) ?? null];
    }
    return [];
  }, [allProjectProposalVersionsData]);

  // Council types
  const { data: councilTypesData } = useQuery(['councilTypes'], getAllCouncilTypes);
  const councilTypeId = useMemo(
    () =>
      councilTypesData?.data?.find(
        (item) => item?.code === COUNCIL_TYPE_CODE.PRELIMINARY_SELECTION_COUNCIL
      )?.id ?? null,
    [councilTypesData]
  );

  // General info of project proposal (section A)
  const { data: generalInfoProjectProposalData } = useQuery(
    ['generalInfoProjectProposal', projectProposalId],
    () => generalInfoForCouncil(projectProposalId),
    {
      staleTime: 1000 * 60 * 5,
      enabled: !!projectProposalId,
    }
  );
  const generalInfoProjectProposal = useMemo(
    () => generalInfoProjectProposalData?.data,
    [generalInfoProjectProposalData]
  );

  const owner = useMemo(
    () =>
      generalInfoProjectProposal?.projectProposalContacts?.find(
        (item) => item?.projectRole?.code === PROJECT_ROLES.OWNER
      ) ?? {},
    [generalInfoProjectProposal]
  );
  // end General info of project proposal (section A)

  const {
    refetch: refetchCouncilMemberExcelFile,
    isLoading: isLoadingCouncilMemberExcelFile,
    isFetching: isFetchingCouncilMemberExcelFile,
  } = useQuery(
    ['downloadCouncilMemberExcelFile', councilId, prequalificationVersion?.id ?? projectProposalId],
    () =>
      downloadCouncilMemberExcelFile(councilId, prequalificationVersion?.id ?? projectProposalId),
    {
      enabled: false,
      onSuccess: (res) => {
        autoDownloadFile(res);
      },
      onError: () => {
        toast(TOAST.ERROR, t('projectProposal.review.exportExcelErrorTitle'));
      },
    }
  );

  // Organizations (to get organization name for every council member)
  const { data: rawOrganizations } = useQuery(['Organizations'], getAllOrganizations, {
    staleTime: 1000 * 60 * 5,
  });
  const organizations = useMemo(
    () =>
      rawOrganizations?.data.map((e) => ({
        ...e,
        name: t('lang') === 'vi' ? e?.name : e?.englishName,
      })) ?? [],
    [rawOrganizations, t]
  );
  // end Organizations

  const { mutate: mutateDownloadFile } = useMutation(downloadFile);
  const {
    // mutate: mutateCreateOrUpdate,
    isLoading: isCreateOrUpdateLoading,
    isError: isCreateOrUpdateError,
    error: createOrUpdateError,
  } = useMutation(createOrUpdatePrequalificationCouncil);
  const {
    // eslint-disable-next-line no-unused-vars
    mutate: mutateChangeRequestStatus,
    isLoading: isChangeRequestStatusLoading,
    isError: isChangeRequestStatusError,
    error: changeRequestStatusError,
  } = useMutation(changeRequestStatus);

  const {
    mutate: mutateUpdateExplanationRequest,
    isLoading: isUpdateExplanationRequestLoading,
    isError: isUpdateExplanationRequestError,
    error: updateExplanationRequestError,
  } = useMutation(updateExplanationRequest);

  const {
    // mutate: mutateRequestExplanation,
    isLoading: isRequestExplanationLoading,
    isError: isRequestExplanationError,
    error: requestExplanationError,
  } = useMutation(requestExplanation);

  // #endregion

  // #region Events
  useEffect(() => {
    const currentValues = watch();
    reset({
      ...currentValues,
      councilStatus: council?.councilStatus ?? council?.projectProposalCouncils?.[0]?.councilStatus,
      reportFileUrl: council?.reportFileUrl ?? council?.projectProposalCouncils?.[0]?.councilStatus,
      reportFile: null,
      comment: explanationRequest?.comment ?? null,
      oldCommentFileUrls: explanationRequest?.commentFileUrls ?? [],
      commentFiles: null,
      // Dùng các field này cho Trường hợp chưa yêu cầu giải trình lần nào
      isOpened: explanationRequest?.isOpened ?? false,
      isEditAllowed: explanationRequest?.isEditAllowed ?? false,
    });

    resetFeedback({
      feedback: explanationRequest?.feedback ?? null,
      isOpened: explanationRequest?.isOpened ?? false,
      isEditAllowed: explanationRequest?.isEditAllowed ?? false,
    });
  }, [council, explanationRequest]);

  const onFeedbackSubmit = (data) => {
    const formData = new FormData();

    formData.append('projectProposalId', projectProposalId);
    formData.append('id', explanationRequest?.id);

    if (data?.feedback) {
      formData.append('feedback', data.feedback);
    }

    formData.append('isOpened', data?.isOpened ? 'true' : 'false');
    formData.append('isEditAllowed', data?.isEditAllowed ? 'true' : 'false');

    // thêm cái này để không mất các commentFileUrls cũ
    if (explanationRequest?.commentFileUrls?.length > 0) {
      formData.append('oldCommentFileUrls', JSON.stringify(explanationRequest?.commentFileUrls));
    }

    // if isSendMailAllowed = false => not send email request explanation
    // else: check some more conditions to send email request explanation
    formData.append('isSendMailAllowed', 'true');

    mutateUpdateExplanationRequest(formData, {
      onSuccess: () => {
        toast(
          TOAST.SUCCESS,
          data?.isOpened
            ? t('projectProposal.requestExplanation.requestUpdateSuccess')
            : t('projectProposal.requestExplanation.addFeedbackSuccess')
        );
        refetchExplanationRequests();
      },
      onError: () => {
        setIsVisibleDialogError(true);
      },
    });
  };

  // eslint-disable-next-line no-shadow
  const getNumberMembersReviewed = (members) => getNumberMembersPresentAndReviewed(members);
  const handleDownloadFile = (path) => {
    mutateDownloadFile(
      {
        projectProposalId,
        path,
      },
      {
        onSuccess: async (res) => {
          if (res?.data) {
            const file = new Blob([res.data]);
            const link = document.createElement('a');
            link.href = URL.createObjectURL(file);
            link.download = formatFileName(path);

            link.click();
          }
        },
      }
    );
  };

  const checkFeedbackValidation = () => {
    if (!isFeedbackValid) {
      toast(TOAST.ERROR, t('errorMessage.validationErrorMessage'));
    }
  };

  const accept = () => {
    navigate(
      `/projects-revise/${projectProposalId}/council/edit/${council?.id}/${
        council?.councilType?.id ?? councilTypeId
      }?key=editResult`
    );
  };

  // const reject = () => {};
  const confirmUpdateResult = () => {
    confirmDialog({
      message: t('council.createCouncilNoMember.confirmationMessage'),
      header: t('council.createCouncilNoMember.confirmationTitle'),
      icon: 'pi pi-exclamation-triangle',
      defaultFocus: 'accept',
      style: { width: '25vw' },
      accept,
      acceptLabel: t('formLayout.action.ok'),
      rejectLabel: t('formLayout.action.cancel'),
    });
  };

  const footerConfirmationPrompt = (onConfirm, onCancel) => (
    <div>
      <Button
        label={t('formLayout.action.cancel')}
        className="w-7rem"
        severity="danger"
        onClick={onCancel}
        autoFocus
      />
      <Button
        label={t('formLayout.action.ok')}
        className="w-7rem"
        severity="info"
        onClick={onConfirm}
      />
    </div>
  );

  // #endregion

  return (
    <>
      {(isCreateOrUpdateLoading ||
        isPrequalificationCouncilLoading ||
        isChangeRequestStatusLoading ||
        isExplanationRequestsLoading ||
        isUpdateProjectTypeDataLoading ||
        isUpdateExplanationRequestLoading ||
        isRequestExplanationLoading) && <Loading />}

      <ErrorDialog
        title="Lỗi lấy thông tin giải trình sơ tuyển"
        isError={isExplanationRequestsError}
        error={explanationRequestsError}
        visible={isVisibleErrorDialog}
        setVisible={setIsVisibleDialogError}
      />
      <ErrorDialog
        title="Lỗi lấy thông tin cập nhật loại đề tài"
        isError={isUpdateProjectTypeDataError}
        error={updateProjectTypeDataError}
        visible={isVisibleErrorDialog}
        setVisible={setIsVisibleDialogError}
      />
      <ErrorDialog
        title="Lỗi lấy thông tin hội đồng sơ tuyển"
        isError={isPrequalificationCouncilError}
        error={prequalificationCouncilError}
        visible={isVisibleErrorDialog}
        setVisible={setIsVisibleDialogError}
      />
      <ErrorDialog
        title="Lỗi cập nhật kết quả sơ tuyển"
        isError={isCreateOrUpdateError}
        error={createOrUpdateError}
        visible={isVisibleErrorDialog}
        setVisible={setIsVisibleDialogError}
      />
      <ErrorDialog
        title="Lỗi chuyển trạng thái yêu cầu giải trình"
        isError={isChangeRequestStatusError}
        error={changeRequestStatusError}
        visible={isVisibleErrorDialog}
        setVisible={setIsVisibleDialogError}
      />
      <ErrorDialog
        title="Lỗi tạo yêu cầu giải trình"
        isError={isRequestExplanationError}
        error={requestExplanationError}
        visible={isVisibleErrorDialog}
        setVisible={setIsVisibleDialogError}
      />
      <ErrorDialog
        title="Lỗi cập nhật yêu cầu giải trình"
        isError={isUpdateExplanationRequestError}
        error={updateExplanationRequestError}
        visible={isVisibleErrorDialog}
        setVisible={setIsVisibleDialogError}
      />

      <SendMailRemindAllMembersDialog
        visible={isVisibleConfirmSendRemind}
        setVisible={setIsVisibleConfirmSendRemind}
        refetch={refetchCouncil}
        council={{
          councilType: {
            code: COUNCIL_TYPE_CODE.PRELIMINARY_SELECTION_COUNCIL,
          },
          ...(council ?? {}),
        }}
      />

      <ReactRouterPrompt when={isDirty && Object.keys(dirtyFields).length}>
        {({ isActive, onConfirm, onCancel }) => (
          <Dialog
            visible={isActive}
            header={<p className="text-blue-500">{t('projectProposal.confirmationTitle')}</p>}
            position="center"
            style={{ width: '300px' }}
            onHide={onCancel}
            draggable={false}
            footer={() => footerConfirmationPrompt(onConfirm, onCancel)}
          >
            <p className="text-lg py-3">{t('projectProposal.confirmationPromptMessage')}</p>
          </Dialog>
        )}
      </ReactRouterPrompt>
      <div className="bg-white p-5">
        {!isReadOnly &&
          checkIsAllowedCRUDCouncil(generalInfoProjectProposal?.projectType, roles) && (
            <div className="flex justify-content-end align-items-center">
              <div className="flex gap-2">
                {((council && council?.councilMembers?.length > 0) || !council) &&
                  !isPrequalificationCouncilLoading && (
                    <>
                      <Can do="read_member" on="council">
                        <Button
                          icon="pi pi-download"
                          severity="success"
                          label={t('council.viewList.downloadCouncilMemberExcel')}
                          tooltipOptions={{ position: 'left' }}
                          onClick={() => {
                            refetchCouncilMemberExcelFile();
                          }}
                          disabled={
                            !council ||
                            council?.councilMembers?.length === 0 ||
                            isLoadingCouncilMemberExcelFile ||
                            isFetchingCouncilMemberExcelFile
                          }
                          loading={
                            isLoadingCouncilMemberExcelFile || isFetchingCouncilMemberExcelFile
                          }
                        />
                      </Can>

                      <Can do="send_mail" on="council">
                        <Button
                          icon="pi pi-envelope"
                          severity="success"
                          tooltip={`${t('council.viewList.sendRemindMember')}
                ${t('council.fields.numberSentMail')}: ${
                            council?.projectProposalCouncils?.[0]?.numberSentMail ?? 0
                          }
                ${t('council.fields.lastSentMail')}: ${formatDatetime(
                            council?.projectProposalCouncils?.[0]?.lastSentMail,
                            'HH:mm:ss DD/MM/YYYY'
                          )}`}
                          tooltipOptions={{ position: 'left' }}
                          onClick={() => setIsVisibleConfirmSendRemind(true)}
                          disabled={!council?.completedAt}
                        />
                      </Can>
                      <Can do="create" on="council">
                        {!council?.deletedAt && !council && (
                          // eslint-disable-next-line react/jsx-no-undef
                          <Link
                            to="/councils/create"
                            state={{ projectProposalId: generalInfoProjectProposal?.reference }}
                            onClick={(e) => {
                              if (
                                !checkIsAllowedCRUDCouncil(
                                  generalInfoProjectProposal?.projectType,
                                  roles
                                )
                              ) {
                                e.preventDefault();
                                toast(TOAST.ERROR, t('permission.forbid.create.council'));
                              }
                            }}
                          >
                            <Button
                              icon="pi pi-plus"
                              severity="primary"
                              tooltip={t('council.viewList.addCouncil')}
                              tooltipOptions={{ position: 'left' }}
                            />
                          </Link>
                        )}
                      </Can>
                      <Can do="update" on="council">
                        {council && !council?.deletedAt && council?.isAllowedToEditCouncil && (
                          // eslint-disable-next-line react/jsx-no-undef
                          <Link
                            to={`/councils/edit/${councilId}`}
                            onClick={(e) => {
                              if (
                                !checkIsAllowedCRUDCouncil(
                                  generalInfoProjectProposal?.projectType,
                                  roles
                                )
                              ) {
                                e.preventDefault();
                                toast(TOAST.ERROR, t('permission.forbid.edit.council'));
                              }
                            }}
                          >
                            <Button
                              icon="pi pi-pencil"
                              severity="warning"
                              tooltip={t('council.viewList.editCouncil')}
                              tooltipOptions={{ position: 'left' }}
                            />
                          </Link>
                        )}
                      </Can>
                    </>
                  )}
                <Can do="update" on="council">
                  <Link
                    to={`/projects-revise/${projectProposalId}/council/edit/${council?.id}/${
                      council?.councilType?.id ?? councilTypeId
                    }?key=editResult`}
                    // className={!council ? 'pointer-events-none' : ''}
                    onClick={(e) => {
                      if (
                        !checkIsAllowedCRUDCouncil(generalInfoProjectProposal?.projectType, roles)
                      ) {
                        e.preventDefault();
                        toast(TOAST.ERROR, t('permission.forbid.create.council'));
                      }
                      if (!council && !council?.members) {
                        e.preventDefault();
                        confirmUpdateResult();
                      }
                    }}
                  >
                    <Button
                      icon="pi pi-check-square"
                      className="bg-teal-600 border-teal-600"
                      tooltip={t('council.viewList.editResult')}
                      tooltipOptions={{ position: 'left' }}
                      // disabled={!council}
                    />
                  </Link>
                </Can>
                {/* {council?.projectProposalCouncils?.[0]?.councilStatus === 'qualified' && (
                  <Can do="update" on="council">
                    <Button
                      severity="help"
                      icon="pi pi-file-edit"
                      tooltip={t('requestUpdateProjectType.label')}
                      tooltipOptions={{ position: 'left' }}
                      onClick={() => setIsVisibleUpdate(true)}
                    />
                    <RequestUpdateType
                      isVisible={isVisibleUpdate}
                      onHide={() => setIsVisibleUpdate(false)}
                      generalInfoProjectProposal={generalInfoProjectProposal}
                    />
                  </Can>
                )} */}
              </div>
            </div>
          )}

        <div className="m-0 bg-white p-5 p-fluid detail-info-council">
          <div className="flex flex-column mb-5">
            <h3
              className="line-height-3 text-4xl text-center font-semibold"
              style={{ marginBottom: '0px', paddingBottom: '1px' }}
            >
              {t('council.view.councilType.prequalificationCouncil').toUpperCase()}
            </h3>

            {council &&
              !council?.completedAt &&
              council?.councilMembers?.length > 0 &&
              !isReadOnly && (
                <h4 className="text-red-500 text-center">
                  ({t('council.view.notDoneAndSentMail')})
                </h4>
              )}
            {council &&
              council?.completedAt &&
              council?.councilMembers?.some((item) => !item?.isSentMail) && (
                <h4 className="text-blue-600 text-center">({t('council.view.onlyDone')})</h4>
              )}
          </div>
          <div className="ml-0 sm:ml-0 md:ml-4 lg:ml-4">
            <div className="pb-2">
              {/* ProjectProposal Infor */}
              <h4 className="mb-3" style={{ color: '#0000ff' }}>
                {t('council.view.overviewLabel').toUpperCase()}
              </h4>
              <div className="ml-4 mb-2 grid text-lg">
                <div className="col">
                  <span className="font-semibold">
                    {t('projectProposal.general.projectNameAndType.vietnameseName')}:{' '}
                  </span>
                  <span className="w-full">{generalInfoProjectProposal?.vietnameseName}</span>
                </div>
              </div>
              <div className="ml-4 mb-2 grid text-lg">
                <div className="col">
                  <span className="font-semibold">
                    {t('projectProposal.general.projectNameAndType.englishName')}:{' '}
                  </span>
                  <span className="w-full">{generalInfoProjectProposal?.englishName}</span>
                </div>
              </div>
              <div className="ml-4 mb-2 grid text-lg">
                <div className="col">
                  <span className="font-semibold">
                    {' '}
                    {t('projectProposal.general.ownerSection.labelCouncil')}:{' '}
                  </span>
                  <span>
                    {`${getAcadamicLevelShortHand(
                      owner?.academicRank,
                      owner?.academicDegree,
                      t,
                      true
                    )}${owner?.fullname ?? generalInfoProjectProposal?.owner?.fullname ?? ''}`}
                  </span>
                </div>
              </div>

              {/* Council info */}
              <div className="text-lg mb-3 mt-3">
                <h4 className="mb-3" style={{ color: '#0000ff' }}>
                  {t('council.view.councilInfoLabel.preliminarySelectionCouncil').toUpperCase()}
                </h4>

                <div className="ml-4 mb-2 grid text-lg">
                  <div className="col">
                    <span className="font-semibold">
                      {t('council.fields.name.specializationCouncil')}:{' '}
                    </span>
                    <span className="w-full">{council?.name}</span>
                  </div>
                </div>

                <div className="ml-4 grid text-lg">
                  <div className="col-12 sm:col-6 md:col-6 lg:col-6">
                    <span className="font-semibold">{t('council.fields.establishDate')}: </span>
                    <span>
                      {council?.timeCreated &&
                        new Date(council?.timeCreated).toLocaleDateString('en-GB', {
                          timeZone: TIMEZONE_VN,
                        })}
                    </span>
                  </div>

                  <div className="col-12 sm:col-6 md:col-6 lg:col-6">
                    <span className="font-semibold">
                      {t('council.fields.decisionToEstablish')}:{' '}
                    </span>
                    {council?.projectProposalCouncils?.[0]?.establishmentFileUrl && (
                      <span
                        className="p-link text-primary underline"
                        onClick={() =>
                          handleDownloadFile(
                            council?.projectProposalCouncils?.[0]?.establishmentFileUrl,
                            council?.id
                          )
                        }
                      >
                        {t('council.view.decision')}
                      </span>
                    )}
                  </div>

                  <div className="col-12 sm:col-6 md:col-6 lg:col-6">
                    <span className="font-semibold">{t('council.fields.reviewDeadline')}: </span>
                    {council?.projectProposalCouncils?.[0]?.reviewDeadline &&
                      new Date(
                        council?.projectProposalCouncils?.[0]?.reviewDeadline
                      ).toLocaleDateString('en-GB', { timeZone: TIMEZONE_VN })}
                  </div>

                  <div className="col-12 sm:col-6 md:col-6 lg:col-6">
                    <span className="font-semibold">{t('council.fields.meetingDate')}: </span>
                    <span className="w-full">
                      {council?.projectProposalCouncils?.[0]?.timeMet &&
                        new Date(council?.projectProposalCouncils?.[0]?.timeMet).toLocaleDateString(
                          'en-GB',
                          { timeZone: TIMEZONE_VN }
                        )}
                    </span>
                  </div>

                  <div className="col-12 sm:col-6 md:col-6 lg:col-6">
                    <span className="font-semibold">
                      {' '}
                      {t('council.fields.numberOfMembersReviewed')}:{' '}
                    </span>
                    <span className="w-full">
                      {getNumberMembersReviewed(council?.councilMembers)}/
                      {getFilterMembersAreAllowedReview(council?.councilMembers)?.length}
                    </span>
                  </div>

                  <div className="col-12 sm:col-6 md:col-6 lg:col-6">
                    <span className="font-semibold">{t('council.fields.result')}: </span>
                    <span>
                      {council?.projectProposalCouncils?.[0]?.councilStatus ===
                        COUNCIL_STATUS.NOT_REVIEWED && (
                        <span>{t('council.viewList.notReviewed')}</span>
                      )}
                      {council?.projectProposalCouncils?.[0]?.councilStatus ===
                        COUNCIL_STATUS.NOT_QUALIFIED && (
                        <span>{t('council.viewList.notQualified')}</span>
                      )}
                      {council?.projectProposalCouncils?.[0]?.councilStatus ===
                        COUNCIL_STATUS.QUALIFIED && <span>{t('council.viewList.qualified')}</span>}
                    </span>
                  </div>
                </div>

                {/* Member Infor */}
                <Can do="read_member" on="council">
                  <div>
                    <h4 className="mb-3" style={{ color: '#0000ff' }}>
                      {t('council.view.councilMemberLabel')}
                    </h4>
                    <div className="ml-4">
                      {!isPrequalificationCouncilLoading && council?.councilMembers && (
                        <Members
                          refetch={refetchCouncil}
                          council={council}
                          members={council?.councilMembers}
                          councilType={council?.councilType}
                          organizations={organizations}
                          projectProposalId={projectProposalId}
                          councilId={council?.id}
                          reviewDeadline={council?.projectProposalCouncils?.[0]?.reviewDeadline}
                          isShowReview
                          isReadOnly={
                            isReadOnly ||
                            !checkIsAllowedCRUDCouncil(
                              generalInfoProjectProposal?.projectType,
                              roles
                            )
                          }
                          yearProposed={
                            parseInt(generalInfoProjectProposal?.yearProposed, 10) || null
                          }
                          template={generalInfoProjectProposal?.template}
                          hasProjects={false}
                          isShowResultReviewPrequalification
                        />
                      )}
                    </div>
                  </div>
                </Can>

                {/* Update project type infor */}
                <div className="flex text-lg mt-2 flex-column">
                  <h4 className="mb-3">{t('council.view.conclusion.updateProjectType')}</h4>
                  {updateProjectType ? (
                    <>
                      <div className="ml-4 mb-2 grid text-lg">
                        <div className="col">
                          <span className="font-semibold">
                            {t('requestUpdateProjectType.oldProjectType')}:{' '}
                          </span>
                          <span className="w-full">
                            {t(
                              `projectTypes.${updateProjectType?.projectProposal?.oldProjectType}`
                            )}
                          </span>
                        </div>
                      </div>
                      <div className="ml-4 mb-2 grid text-lg">
                        <div className="col">
                          <span className="font-semibold">
                            {t('requestUpdateProjectType.newProjectType')}:{' '}
                          </span>
                          {t(`projectTypes.${updateProjectType?.projectProposal?.projectType}`)}
                        </div>
                      </div>
                      <div className="ml-4 mb-2 grid text-lg">
                        <div className="col">
                          <span className="font-semibold">
                            {t('requestUpdateProjectType.dateUpdate')}:{' '}
                          </span>
                          {updateProjectType?.updatedAt &&
                            new Date(updateProjectType?.updatedAt).toLocaleDateString('en-GB', {
                              timeZone: TIMEZONE_VN,
                            })}
                        </div>
                      </div>
                      <div className="ml-4 mb-2 grid text-lg">
                        <div className="col">
                          <span className="font-semibold">
                            {t('requestUpdateProjectType.note')}:{' '}
                          </span>
                          {updateProjectType?.ownerComment}
                        </div>
                      </div>
                    </>
                  ) : (
                    <div className="ml-4 mb-2 grid text-lg">
                      <div className="col">
                        <span className="">
                          <i> {t('requestUpdateProjectType.notUpdated')}</i>
                        </span>
                      </div>
                    </div>
                  )}
                </div>

                {/* General conclusion */}
                <div>
                  <h4 className="mb-3">{t('council.view.conclusion.specializationCouncil')}</h4>
                  {council?.projectProposalCouncils?.[0]?.isApproved === false && (
                    <div className="ml-4">
                      <Message
                        severity="error"
                        text={t('council.view.refuse')}
                        className="w-max font-semibold"
                      />
                      <p className="mt-3">{t('council.view.reason')}</p>
                      <RichTextViewer
                        style={{ minHeight: '200px', width: '100%' }}
                        value={council?.projectProposalCouncils?.[0]?.conclusion}
                        showHeader={false}
                        readOnly
                      />
                    </div>
                  )}
                  {council?.projectProposalCouncils?.[0]?.isApproved === true && (
                    <div className="ml-4">
                      <Message
                        severity="success"
                        text={t('council.view.approve')}
                        className="w-max font-semibold"
                      />
                      <p className="mt-3">{t('council.view.opinion')}:</p>
                      <p className="font-italic mt-4">{t('council.view.opinionDetail')}</p>
                      <RichTextViewer
                        style={{ minHeight: '200px', width: '100%' }}
                        value={council?.projectProposalCouncils?.[0]?.conclusion}
                        showHeader={false}
                        readOnly
                      />
                    </div>
                  )}
                </div>
              </div>

              {/* Result minutes */}
              <div className="flex text-lg mt-2 flex-column">
                <h4 className="mb-3">
                  {t(
                    'council.view.appraisalCouncilMinutesLabel.preliminarySelectionCouncil'
                  ).toUpperCase()}
                </h4>
                <div className="flex ml-5 flex-column">
                  <div className="mb-2">
                    {council?.projectProposalCouncils?.[0]?.reportFileUrl ? (
                      <span
                        className="p-link text-primary underline"
                        onClick={() =>
                          handleDownloadFile(
                            council?.projectProposalCouncils?.[0]?.reportFileUrl,
                            council?.id
                          )
                        }
                      >
                        {t('council.view.appraisalCouncilMinutes')}
                      </span>
                    ) : (
                      <span className="font-italic">
                        {t('council.view.noAppraisalCouncilMinutes')}
                      </span>
                    )}
                  </div>
                </div>
              </div>

              {/* Specialist's comment */}
              <div className="flex text-lg mt-2 flex-column">
                <h4 className="mb-3">
                  {t('projectProposal.requestExplanation.specialistComment')}
                </h4>
                <div className="flex ml-5 flex-column">
                  <div className="mb-2">
                    {explanationRequest?.comment && (
                      <p
                        className="font-italic text-justify m-0"
                        style={{ whiteSpace: 'pre-wrap' }}
                      >
                        {explanationRequest?.comment}
                      </p>
                    )}
                  </div>
                </div>
              </div>

              {/* Owner Explanation */}
              {checkIsAllowedCRUDCouncil(generalInfoProjectProposal?.projectType, roles) && (
                <OwnerExplanation
                  control={feedbackControl}
                  errors={feedbackErrors}
                  isDirty={isFeedbackDirty}
                  dirtyFields={feedbackDirtyFields}
                  handleSubmit={handleSubmitFeedback(onFeedbackSubmit)}
                  checkValidation={checkFeedbackValidation}
                  handleDownloadFile={handleDownloadFile}
                  explanationRequest={explanationRequest}
                  correspondingVersion={prequalificationVersion}
                  council={council}
                  isReadOnly={isReadOnly}
                />
              )}

              <ConfirmDialog />
            </div>
          </div>
        </div>
      </div>
    </>
  );
}
